// Export & Share dropdown menu for a single spiritual download
import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Download,
  Share2,
  Copy,
  FileDown,
  Share,
  Loader2,
  Check,
} from 'lucide-react';
import {
  exportAsPDF,
  copyAsText,
  sharePlatform,
  canNativeShare,
} from '../utils/exportShare';

export const ExportShareMenu = ({ entry, variant = 'default', size = 'sm' }) => {
  const [busy, setBusy] = useState(null); // 'pdf' | 'copy' | 'share' | null
  const [justCopied, setJustCopied] = useState(false);

  if (!entry) return null;

  const guardedRun = async (kind, fn) => {
    if (busy) return;
    setBusy(kind);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const handlePDF = () =>
    guardedRun('pdf', async () => {
      try {
        const filename = exportAsPDF(entry);
        toast.success('PDF downloaded', {
          description: filename,
        });
      } catch (err) {
        console.error('PDF export failed:', err);
        toast.error('Could not generate PDF', {
          description: err?.message || 'Please try again.',
        });
      }
    });

  const handleCopy = () =>
    guardedRun('copy', async () => {
      try {
        await copyAsText(entry);
        setJustCopied(true);
        toast.success('Copied to clipboard', {
          description: 'Your download is ready to paste anywhere.',
        });
        setTimeout(() => setJustCopied(false), 2200);
      } catch (err) {
        console.error('Copy failed:', err);
        toast.error('Could not copy to clipboard');
      }
    });

  const handleShare = () =>
    guardedRun('share', async () => {
      try {
        const result = await sharePlatform(entry);
        if (result.method === 'native') {
          if (result.ok) {
            toast.success('Shared successfully');
          }
        } else if (result.method === 'clipboard-fallback') {
          toast.success('Copied to clipboard', {
            description:
              'Sharing is not available on this device; we copied the download instead.',
          });
        }
      } catch (err) {
        console.error('Share failed:', err);
        toast.error('Could not share', {
          description: err?.message || 'Please try again.',
        });
      }
    });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === 'primary' ? 'default' : 'outline'}
          size={size}
          data-testid="export-share-trigger"
          className={
            variant === 'primary'
              ? 'bg-[var(--sd-primary)] hover:bg-[var(--sd-primary-dark)] text-[var(--sd-primary-fg)]'
              : 'border-[var(--sd-primary)] text-[var(--sd-primary)] hover:bg-[var(--sd-primary)]/10'
          }
          disabled={!!busy}
        >
          {busy ? (
            <>
              <Loader2 size={14} className="mr-2 animate-spin" />
              Working…
            </>
          ) : (
            <>
              <Share2 size={14} className="mr-2" />
              Export & Share
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56 bg-white border-[var(--sd-border)]"
      >
        <DropdownMenuLabel className="text-[var(--sd-fg-secondary)] font-semibold">
          Keep or share this download
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handlePDF}
          data-testid="export-pdf-item"
          className="cursor-pointer focus:bg-[var(--sd-primary)]/10 focus:text-[var(--sd-fg)]"
        >
          <FileDown size={16} className="mr-2 text-[var(--sd-primary)]" />
          <div className="flex flex-col">
            <span className="text-sm font-medium">Download as PDF</span>
            <span className="text-xs text-[var(--sd-muted-fg)]">
              Beautifully formatted, ready to print
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={handleCopy}
          data-testid="copy-as-text-item"
          className="cursor-pointer focus:bg-[var(--sd-primary)]/10 focus:text-[var(--sd-fg)]"
        >
          {justCopied ? (
            <Check size={16} className="mr-2 text-[var(--sd-success)]" />
          ) : (
            <Copy size={16} className="mr-2 text-[var(--sd-primary)]" />
          )}
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {justCopied ? 'Copied!' : 'Copy as text'}
            </span>
            <span className="text-xs text-[var(--sd-muted-fg)]">
              Paste into journal, notes, or email
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleShare}
          data-testid="share-platform-item"
          className="cursor-pointer focus:bg-[var(--sd-primary)]/10 focus:text-[var(--sd-fg)]"
        >
          <Share size={16} className="mr-2 text-[var(--sd-primary)]" />
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {canNativeShare() ? 'Share…' : 'Share (copy)'}
            </span>
            <span className="text-xs text-[var(--sd-muted-fg)]">
              {canNativeShare()
                ? 'Use your device’s share sheet'
                : 'Falls back to copying to clipboard'}
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportShareMenu;
